/* ==========================================================================
   Trade page behaviour
   - Manufacturing / Retail / Foodservice tab switching (hero + sticky bar)
   - Deep linking via #retail / #manufacturing / #foodservice
   - CTAs that jump to the enquiry form and pre-select the enquiry type
   - Enquiry form: reveal the NPD fields only when they are relevant

   Everything degrades gracefully: without JS all three panels render
   stacked and the form shows every field.
   ========================================================================== */
(function () {
  'use strict';

  var PANELS = ['retail', 'manufacturing', 'foodservice'];
  var DEFAULT_PANEL = 'manufacturing';
  var STORAGE_KEY = 'halenmon:trade:panel';

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function isValidPanel(name) {
    return PANELS.indexOf(name) !== -1;
  }

  /* Option values are human-readable because they land in the notification
     email; the stable key used for linking lives in data-key. */
  function optionKey(option) {
    return option.getAttribute('data-key') || option.value;
  }

  /* Offset any scroll target by the theme's sticky header, which overlays
     the top of the viewport once it is stuck. The trade switcher itself is
     bottom-anchored, so it never eats into the target. */
  function stickyOffset() {
    var header = document.querySelector('.site-header--stuck, .site-header--sticky, .header-wrapper--sticky');
    var offset = header ? header.offsetHeight : 0;
    return offset + 16;
  }

  function scrollToEl(el, extraOffset) {
    if (!el) return;
    var top = window.pageYOffset + el.getBoundingClientRect().top - stickyOffset() - (extraOffset || 0);
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  var Trade = {
    current: null,

    /* ---------------------------------------------------------------- */

    setPanel: function (name, options) {
      options = options || {};
      if (!isValidPanel(name)) name = DEFAULT_PANEL;
      if (name === this.current && !options.force) return;

      this.current = name;

      qsa('[data-trade-panel]').forEach(function (panel) {
        var match = panel.getAttribute('data-trade-panel') === name;
        panel.classList.toggle('is-active', match);
        panel.setAttribute('aria-hidden', match ? 'false' : 'true');
      });

      qsa('[data-trade-tab]').forEach(function (tab) {
        var match = tab.getAttribute('data-trade-tab') === name;
        if (tab.getAttribute('role') === 'tab') {
          /* Roving tabindex, per the WAI-ARIA tabs pattern. */
          tab.setAttribute('aria-selected', match ? 'true' : 'false');
          tab.setAttribute('tabindex', match ? '0' : '-1');
        } else {
          /* Sticky bar buttons are plain nav controls — every one of them
             stays reachable by keyboard. */
          if (match) {
            tab.setAttribute('aria-current', 'true');
          } else {
            tab.removeAttribute('aria-current');
          }
        }
      });

      try {
        window.sessionStorage.setItem(STORAGE_KEY, name);
      } catch (e) {
        /* private browsing — not important enough to care about */
      }

      if (options.updateHash !== false && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + name);
      }

      document.dispatchEvent(
        new CustomEvent('trade:panelchange', { detail: { panel: name } })
      );
    },

    /* ---------------------------------------------------------------- */

    initTabs: function () {
      document.addEventListener('click', function (event) {
        var tab = event.target.closest('[data-trade-tab]');
        if (tab) {
          event.preventDefault();
          var name = tab.getAttribute('data-trade-tab');
          Trade.setPanel(name);
          if (tab.hasAttribute('data-trade-scroll')) {
            scrollToEl(document.querySelector('[data-trade-panel="' + name + '"]'));
          }
          return;
        }

        /* "Learn about X" style links that live inside another panel */
        var jump = event.target.closest('[data-trade-goto]');
        if (jump) {
          event.preventDefault();
          Trade.setPanel(jump.getAttribute('data-trade-goto'));
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });

      /* Left/right arrow keys move between tabs, per WAI-ARIA tabs pattern */
      qsa('[data-trade-tablist]').forEach(function (list) {
        list.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          var tabs = qsa('[data-trade-tab]', list);
          var index = tabs.indexOf(document.activeElement);
          if (index === -1) return;
          event.preventDefault();
          var next = event.key === 'ArrowRight' ? index + 1 : index - 1;
          if (next < 0) next = tabs.length - 1;
          if (next >= tabs.length) next = 0;
          tabs[next].focus();
          Trade.setPanel(tabs[next].getAttribute('data-trade-tab'));
        });
      });
    },

    /* ---------------------------------------------------------------- */

    initStickyBar: function () {
      var bar = document.querySelector('.trade-stickytabs');
      var sentinel = document.querySelector('[data-trade-tablist]');
      if (!bar || !sentinel || !('IntersectionObserver' in window)) return;

      new IntersectionObserver(
        function (entries) {
          bar.classList.toggle('is-visible', !entries[0].isIntersecting);
        },
        { rootMargin: '-80px 0px 0px 0px', threshold: 0 }
      ).observe(sentinel);
    },

    /* ---------------------------------------------------------------- */

    initEnquiryLinks: function () {
      document.addEventListener('click', function (event) {
        var link = event.target.closest('[data-trade-enquiry]');
        if (!link) return;

        var form = document.querySelector('[data-trade-form]');
        if (!form) return; /* let the href do its thing */

        event.preventDefault();

        var select = form.querySelector('[data-trade-enquiry-type]');
        var value = link.getAttribute('data-trade-enquiry');
        if (select && value) {
          var matched = Array.prototype.filter.call(select.options, function (option) {
            return optionKey(option) === value;
          })[0];
          if (matched) {
            select.value = matched.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        scrollToEl(form.closest('.trade-block') || form);

        /* Move focus to the first field once the scroll has settled, so
           keyboard and screen-reader users land in the right place. */
        window.setTimeout(function () {
          var first = form.querySelector('input, select, textarea');
          if (first) first.focus({ preventScroll: true });
        }, 550);
      });
    },

    /* ---------------------------------------------------------------- */

    initForm: function () {
      qsa('[data-trade-form]').forEach(function (form) {
        var select = form.querySelector('[data-trade-enquiry-type]');
        var npd = form.querySelector('[data-trade-npd]');
        if (!select || !npd) return;

        function sync() {
          var selected = select.options[select.selectedIndex];
          var key = selected ? optionKey(selected) : '';
          var show = npd.getAttribute('data-trade-npd').split(' ').indexOf(key) !== -1;
          npd.hidden = !show;
          qsa('input, select, textarea', npd).forEach(function (field) {
            field.disabled = !show;
          });
        }

        select.addEventListener('change', sync);
        sync();
      });
    },

    /* ---------------------------------------------------------------- */

    /* Pre-tick the newsletter's trade category to match the open tab, but
       never override a choice the visitor has already made themselves. */
    initNewsletter: function () {
      var form = document.querySelector('[data-trade-newsletter]');
      if (!form) return;

      var chosenByVisitor = false;
      qsa('input[type="radio"]', form).forEach(function (radio) {
        radio.addEventListener('change', function () {
          chosenByVisitor = true;
        });
      });

      document.addEventListener('trade:panelchange', function (event) {
        if (chosenByVisitor) return;
        var match = form.querySelector(
          'input[type="radio"][data-panel="' + event.detail.panel + '"]'
        );
        if (match) match.checked = true;
      });
    },

    /* ---------------------------------------------------------------- */

    initialPanel: function () {
      var hash = (window.location.hash || '').replace('#', '').toLowerCase();
      if (isValidPanel(hash)) return hash;

      var param = new URLSearchParams(window.location.search).get('trade');
      if (param && isValidPanel(param.toLowerCase())) return param.toLowerCase();

      /* Coming back from a form submission? Stay where they were. */
      try {
        var stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (stored && isValidPanel(stored)) return stored;
      } catch (e) {
        /* ignore */
      }

      return DEFAULT_PANEL;
    },

    init: function () {
      if (!document.querySelector('[data-trade-panel]')) return;

      this.initTabs();
      this.initStickyBar();
      this.initEnquiryLinks();
      this.initForm();
      this.initNewsletter();
      this.setPanel(this.initialPanel(), { updateHash: false, force: true });

      window.addEventListener('hashchange', function () {
        var hash = (window.location.hash || '').replace('#', '').toLowerCase();
        if (isValidPanel(hash)) Trade.setPanel(hash, { updateHash: false });
      });
    }
  };

  window.HalenMonTrade = Trade;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      Trade.init();
    });
  } else {
    Trade.init();
  }

  /* Theme editor: sections are re-rendered in place, so re-apply state. */
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target.querySelector('[data-trade-panel], [data-trade-form], [data-trade-tablist]')) {
      return;
    }
    Trade.initForm();
    Trade.initStickyBar();
    Trade.setPanel(Trade.current || Trade.initialPanel(), { updateHash: false, force: true });
  });

  /* Theme editor: selecting a hidden panel's section should reveal it. */
  document.addEventListener('shopify:section:select', function (event) {
    var panel = event.target.querySelector('[data-trade-panel]');
    if (panel) Trade.setPanel(panel.getAttribute('data-trade-panel'), { updateHash: false });
  });
})();
